"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/hooks/useRequestPortfolioActions";

import { useCallback, useRef } from "react";
import { fetchChecklist } from "@/src/store/modules/setupChecklist";
import {
  REQUEST_PORTFOLIO_ACTION,
  type RequestPortfolioCardAction,
} from "@components/ChatOnboarding/requestPortfolioAction";
import type { PlaidAccountCategory } from "@components/PlaidConnectFlow/types";
// import { useMemoizedFn } from "ahooks";
import { OspToast } from "@ospweb/sdk-ui";
import { IS_PLAID_OPEN } from "@components/PlaidImportModal/openPlaidImportModal";
import { useAccountConnectionFlow } from "@/src/hooks/useAccountConnectionFlow";
import { buildPortfolioScreenshotChatInfo } from "@/src/hooks/onboarding/portfolio/chat/buildScreenshotChatInfo";
import type { PortfolioUploadType } from "@components/PortfolioScreenshotModal/types";
import type { SenderAttachment } from "@components/Sender/type";
import { Track } from "@/src/lib/tracking";
import { OVERLAP_REQUEST_CARD_FLOW_PARAMS } from "@features/etf-overlap/constants/flowParams";
import { overlapAttributionParams } from "@features/etf-overlap/tracking/overlapAttribution";

const I18n = I18nUtil.createScoped(Translations);

export { REQUEST_PORTFOLIO_ACTION as REQUEST_PORTFOLIO_SUBMIT_ACTION };

const REQUEST_PORTFOLIO_PLAID_SUCCESS_MESSAGE =
  "Plaid account connected successfully. Asset data may be available in 2-3 minutes.";

export interface UseRequestPortfolioActionsOptions {
  /**
   * 提交 role:tool 消息（Message.tsx 的 submitInteractiveToolMessage）。
   * options.attachments 随消息一起上送。
   */
  onSubmit: (
    content: string,
    options?: { attachments?: SenderAttachment[] },
  ) => void;
  /** Chat 大类卡入口（EDG-591），透传给 `plaid_link_*` 的 `entry` */
  trackingEntry?: string;
  /**
   * 仅重叠分析消息传入（EDG-561）：`result` = 结果页下的连账户卡，`insufficient` = ETF 不足页。
   * 决定是否带重叠链路参数与 `overlap_request_card_tap` 埋点；缺省 = 线上行为完全不变。
   */
  overlapOrigin?: "result" | "insufficient";
}

/** 全部为图片 → "image"（screenshot_recognized）；含任意非图片文件 → "pdf"（pdf_recognized） */
function resolveUploadTypeFromAttachments(
  attachments: SenderAttachment[],
): PortfolioUploadType {
  const allImages = attachments.every((item) =>
    item.mediaType?.startsWith("image/"),
  );
  return allImages ? "image" : "pdf";
}

export const useRequestPortfolioActions = ({
  onSubmit,
  trackingEntry,
  overlapOrigin,
}: UseRequestPortfolioActionsOptions) => {
  /**
   * 绑定成功回调发生在连接链路深处，拿不到本次点击的账户类别；
   * 用 ref 在点击时记录，onBindSuccess 读取后生成对应 action 的 tool message。
   */
  const pendingCategoryRef = useRef<PlaidAccountCategory>("invest");

  // 使用账户连接编排 hook
  const { startFlow } = useAccountConnectionFlow({
    hasSelectedPortfolio: true,
    // Chat 内绑定成功后只提交 tool message 交回 Ed 续流，
    // 不自动打开账户管理弹窗（其他入口默认行为不变）
    autoOpenManageModal: false,
    onBindSuccess: async () => {
      onSubmit(
        JSON.stringify({
          action:
            pendingCategoryRef.current === "bank"
              ? REQUEST_PORTFOLIO_ACTION.CONNECT_BANK
              : REQUEST_PORTFOLIO_ACTION.CONNECT_INVESTMENT,
          message: REQUEST_PORTFOLIO_PLAID_SUCCESS_MESSAGE,
        }),
      );
    },
    onUploadSuccess: (attachments) => {
      // EDG-591：上传后刷新 Chat 连接入口的账户状态（状态切换不需刷新页面）
      void fetchChecklist(true);
      // 与源弹窗 PortfolioScreenshotModal 对齐：chatInfo 序列化为 tool message
      // content，instruction 引导 Agent 检查附件；attachments 走第二参数上送。
      onSubmit(
        JSON.stringify(
          buildPortfolioScreenshotChatInfo({
            uploadType: resolveUploadTypeFromAttachments(attachments),
          }),
        ),
        { attachments },
      );
    },
    paywallCopyOverrides: {
      title: I18n.t("Answers from your real numbers. Unlock full access."),
      subtitle: I18n.t(
        "Link an account and I'll answer with your actual data.",
      ),
      features: [
        I18n.t("Track holdings, prices and returns in real time"),
        I18n.t("Net worth, spending and accounts in one place"),
        I18n.t("Flag risks before they land"),
      ],
    },
  });

  const connectAccount = useCallback(
    async (category: PlaidAccountCategory) => {
      if (!IS_PLAID_OPEN) {
        OspToast.info({
          content: I18n.t("Coming soon, please choose other ways."),
        });
        return;
      }

      pendingCategoryRef.current = category;
      // 重叠分析只关心投资账户；银行卡类别仍走线上流程
      const overlapFlow = overlapOrigin && category === "invest";
      if (overlapFlow) {
        // 命令式：下游是 async 的 Plaid 流程，且 `from` 是运行时判定；事件名字面量
        Track.event("overlap_request_card_tap", {
          ...overlapAttributionParams(),
          from: overlapOrigin,
        });
      }
      // 传 accountType：PlaidConnectFlow 跳过类别选择页，直接进 INVEST_LIST / BANK_LIST
      await startFlow({
        accountType: category,
        plaidTrackingContext: trackingEntry
          ? { entry: trackingEntry }
          : undefined,
        ...(overlapFlow ? OVERLAP_REQUEST_CARD_FLOW_PARAMS : {}),
      });
    },
    [overlapOrigin, startFlow, trackingEntry],
  );

  const handleAction = useCallback(
    async (action: RequestPortfolioCardAction) => {
      switch (action) {
        case REQUEST_PORTFOLIO_ACTION.CONNECT_INVESTMENT:
          await connectAccount("invest");
          return;
        case REQUEST_PORTFOLIO_ACTION.CONNECT_BANK:
          await connectAccount("bank");
          return;
        default:
          return;
      }
    },
    [connectAccount],
  );

  return {
    handleAction,
  };
};
